import Button from 'tio-reimagined/components/button';
import { dropTask } from 'ember-concurrency';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, Section, Header } from 'tio-ui/components/layout';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import Component from '@glimmer/component';
import SignaturePad from 'tio-common/components/signature-pad';
import type { PointGroup } from 'signature_pad';
import type EmployeeModel from 'tio-common/models/employee';
import type Owner from '@ember/owner';

interface AdminPslfSignRouteSignature {
  Args: {
    model: EmployeeModel;
  };
}

export default class AdminPslfSignRoute extends Component<AdminPslfSignRouteSignature> {
  @tracked signaturePoints: PointGroup[] = [];
  @tracked signatureImage = '';
  @tracked errors: string[] = [];
  @tracked isSignatureSaved = false;
  @tracked showOverwriteOption = false;

  constructor(owner: Owner, args: AdminPslfSignRouteSignature['Args']) {
    super(owner, args);
    this.showOverwriteOption = this.hasExistingSignature;
  }

  get existingSignature() {
    return this.args.model.pslfProfile?.signature;
  }

  get hasExistingSignature() {
    return !!this.existingSignature;
  }

  get currentSignatureImage() {
    return this.signatureImage || this.existingSignature;
  }

  get hasSignatureToShow() {
    return !!this.currentSignatureImage;
  }

  onSignatureUpdate = (points: PointGroup[], signatureUrl: string) => {
    this.signaturePoints = points;
    this.signatureImage = signatureUrl;
    this.errors = [];
  };

  saveSignature = dropTask(async () => {
    this.errors = [];

    if (!this.signatureImage) {
      this.errors = ['Please provide a signature before saving.'];
      return;
    }

    const hasSignatureComplexity = this.signaturePoints.some((group) => group.points.length > 5); // anything less than 5 is a dot
    if (!hasSignatureComplexity) {
      this.errors = ['Please provide a more detailed signature.'];
      return;
    }

    try {
      this.args.model.pslfProfile.signature = this.signatureImage;
      await this.args.model.pslfProfile.save();
      this.isSignatureSaved = true;
    } catch (error) {
      this.errors = ['Failed to save signature. Please try again.'];
      console.error('Error saving signature:', error);
    }
  });

  clearSignature = () => {
    this.signaturePoints = [];
    this.signatureImage = '';
    this.errors = [];
  };

  enableOverwrite = () => {
    this.showOverwriteOption = false;
    this.clearSignature();
  };

  updateSignature = () => {
    this.isSignatureSaved = false;
  };

  <template>
    <VStack>
      <Header>{{t "special-programs.pslf.sign.header"}}</Header>
      {{#if this.isSignatureSaved}}
        <Section data-test="success-section">
          <:header>
            <span class="text-green-800 font-semibold">{{t
                "pslf.sign.signature_saved_successfully"
              }}</span>
          </:header>
          <:body>
            <AlertBanner @intent="success">
              <p class="text-gray-700 mb-4">
                {{t "pslf.sign.signature_saved_message"}}
              </p>
              {{#if this.hasSignatureToShow}}
                <VStack>
                  <p class="text-sm text-gray-600 font-medium">{{t
                      "pslf.sign.your_current_signature"
                    }}</p>
                  <div class="border border-gray-300 rounded bg-white p-4 max-h-24 max-w-48">
                    <img
                      style={{htmlSafe "aspect-ratio: 7 / 3.5;"}}
                      src={{this.existingSignature}}
                      alt={{t "pslf.sign.your_current_signature_alt"}}
                      data-test="existing-signature-image"
                    />
                  </div>
                </VStack>
              {{/if}}
              <div class="flex space-x-2">
                <LinkTo
                  @route="authenticated.admin.pslf.forms"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  data-test="btn-return-to-dashboard"
                >
                  {{t "pslf.sign.return_to_dashboard"}}
                </LinkTo>

                <Button
                  @appearance="outlined"
                  {{on "click" this.updateSignature}}
                  data-test="btn-update-signature"
                >
                  {{t "pslf.sign.update_signature"}}
                </Button>
              </div>
            </AlertBanner>
          </:body>
        </Section>
      {{else if this.showOverwriteOption}}
        <Section data-test="existing-signature-section">
          <:header>{{t "pslf.sign.existing_signature_found"}}</:header>
          <:body>
            <VStack class="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p class="text-gray-700 mb-4">
                {{t "pslf.sign.existing_signature_message"}}
              </p>
              {{#if this.existingSignature}}
                <VStack>
                  <p class="text-sm text-gray-600 font-medium">{{t
                      "pslf.sign.your_current_signature"
                    }}</p>
                  <div class="border border-gray-300 rounded bg-white p-4 max-h-24 max-w-48">
                    <img
                      style={{htmlSafe "aspect-ratio: 7 / 3.5;"}}
                      src={{this.existingSignature}}
                      alt={{t "pslf.sign.your_current_signature_alt"}}
                      data-test="existing-signature-image"
                    />
                  </div>
                </VStack>
              {{/if}}
              <div class="flex space-x-2">
                <LinkTo
                  @route="authenticated.admin.pslf.forms"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  data-test="btn-keep-existing"
                >
                  {{t "pslf.sign.return_to_dashboard"}}
                </LinkTo>
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.enableOverwrite}}
                  data-test="btn-create-new"
                >
                  {{t "pslf.sign.create_new_signature"}}
                </Button>
              </div>
            </VStack>
          </:body>
        </Section>
      {{else}}
        <Section data-test="signature-creation-section">
          <:header>{{t "pslf.sign.create_digital_signature"}}</:header>
          <:body>
            <VStack class="text-center items-center">
              <p class="text-gray-700">
                {{t "pslf.sign.create_signature_message"}}
              </p>
              <p class="text-sm text-gray-600">
                {{t "pslf.sign.drawing_instructions"}}
              </p>
              <SignaturePad
                @onSignatureUpdate={{this.onSignatureUpdate}}
                @errors={{this.errors}}
                data-test="signature-pad"
              />
              <Button
                @intent="primary"
                @isRunning={{this.saveSignature.isRunning}}
                {{on "click" this.saveSignature.perform}}
                class="max-w-48"
                data-test="btn-save-signature"
              >
                {{t "pslf.sign.save_signature"}}
              </Button>
            </VStack>
          </:body>
        </Section>
      {{/if}}
    </VStack>
  </template>
}
